import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { Plus, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import { useAddAgentDialog } from "@/components/AddAgentDialog";
import { PromoLeaderboardRow } from "@/components/AdvertiseCta";
import { VerifiedBadge } from "@/components/agents/VerifiedBadge";
import { HomepageSearch, InitialAvatar } from "@/components/HomepageSearch";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import { useSellStartupDialog } from "@/components/SellStartupDialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import {
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "@/lib/agent-categories";
import { type Currency, currencyAtom } from "@/lib/currency";
import {
	formatCompactCurrency as formatCompactCurrencyUtil,
	formatCurrency as formatCurrencyUtil,
} from "@/lib/format";
import { homepageLeaderboardQueryOptions } from "@/lib/leaderboard.query";
import type { PublicAgentView } from "@/lib/public-agents";
import { buildLocalizedSeoLinks, ogImageUrl } from "@/lib/seo";
import { toast } from "@/lib/toast";

const SELL_STARTUP_FLAG = "sell-startup";

const METRIC_OPTIONS = [
	{ id: "mrr", label: "MRR" },
	{ id: "arr", label: "ARR" },
	{ id: "growth", label: "Growth" },
] as const;

const PERIOD_OPTIONS = [
	{ id: "all", label: "All time" },
	{ id: "d30", label: "30 days" },
] as const;

type MetricOption = (typeof METRIC_OPTIONS)[number]["id"];
type PeriodOption = (typeof PERIOD_OPTIONS)[number]["id"];
type SnapshotRecord = {
	createdAt: Date | string;
	currency?: string;
	estimatedMrr: number;
	grossRevenue: number;
	id: string;
	snapshotDate: Date | string;
};
type AgentRecord = PublicAgentView & {
	createdAt: Date | string;
	isPublic?: boolean;
	isStealth?: boolean | null;
	lastVerifiedAt?: Date | string | null;
	publishedAt?: Date | string | null;
	revenueSnapshots?: SnapshotRecord[];
};
type LeaderboardRow = {
	categories: string[];
	displayName: string;
	displayDescription: string;
	displayFounderName: string;
	displayFounderSlug: string | null;
	displayFounderAvatarUrl: string | null;
	displayAvatarMode: PublicAgentView["displayAvatarMode"];
	id: string;
	isUnclaimed: boolean;
	isVerified: boolean;
	verificationProvider: string | null;
	logoUrl?: string | null;
	metric: {
		arr: number;
		allTimeRevenue: number;
		mrr: number;
	};
	growth: {
		all: number;
		d30: number;
	};
	mrrDelta: {
		all: number;
		d30: number;
	};
	slug: string;
	snapshots: SnapshotRecord[];
	firstChargeDate?: Date | string | null;
	updatedAtLabel: string | null;
	lastSyncedAt?: Date | string;
};

export const Route = createFileRoute("/")({
	component: HomePage,
	validateSearch: (search: Record<string, unknown>) => ({
		unsubscribed:
			typeof search.unsubscribed === "string" && search.unsubscribed
				? search.unsubscribed
				: undefined,
	}),
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(homepageLeaderboardQueryOptions),
	head: ({ matches }) => ({
		meta: [
			{ title: "Revenue Leaderboard for AI Agents - AgentMRR" },
			{
				name: "description",
				content:
					"The public MRR leaderboard for AI agents. Provider-verified revenue, category rankings, Agent Auth access, and 10 sponsor slots.",
			},
			{
				property: "og:title",
				content: "Revenue Leaderboard for AI Agents - AgentMRR",
			},
			{
				property: "og:description",
				content:
					"Discover, verify, and rank money-making AI agents. AgentMRR adds provider-backed proof, Agent Auth access, and sponsor inventory around the leaderboard.",
			},
			{ property: "og:image", content: ogImageUrl("default") },
			{ property: "og:type", content: "website" },
			{ property: "og:site_name", content: "AgentMRR" },
			{ property: "og:locale", content: "en_US" },
			{ name: "twitter:card", content: "summary_large_image" },
			{ name: "twitter:image", content: ogImageUrl("default") },
		],
		links: buildLocalizedSeoLinks({ matches, internalPath: "/" }),
	}),
});

function HomePage() {
	const [metric, setMetric] = useState<MetricOption>("mrr");
	const [period, setPeriod] = useState<PeriodOption>("all");
	const [visibleRows, setVisibleRows] = useState(25);
	const { openAddAgentDialog } = useAddAgentDialog();
	const { openSellStartupDialog } = useSellStartupDialog();
	const { unsubscribed } = Route.useSearch();

	useEffect(() => {
		if (!unsubscribed) return;
		if (unsubscribed === "1") {
			toast.success("You've been unsubscribed from Radar.");
		} else {
			toast.error("Unsubscribe link is invalid or already used.");
		}
	}, [unsubscribed]);

	const { data: leaderboardData, isLoading: isLeaderboardLoading } = useQuery(
		homepageLeaderboardQueryOptions,
	);

	const allAgents = (leaderboardData?.agents ?? []) as AgentRecord[];
	const rows = buildLeaderboardRows(allAgents).filter((row) =>
		Boolean(row.logoUrl),
	);

	const handleMetricChange = (nextMetric: MetricOption) => {
		setMetric(nextMetric);
		setVisibleRows(25);
	};

	const handlePeriodChange = (nextPeriod: PeriodOption) => {
		setPeriod(nextPeriod);
		setVisibleRows(25);
	};

	return (
		<PublicPageLayout>
			<div>
				<section className="relative bg-background">
					<div className="relative mx-auto w-full max-w-5xl px-0 pt-8 sm:pt-12">
						<HeroTerminal
							onAddAgent={openAddAgentDialog}
							onSellStartup={openSellStartupDialog}
						/>
					</div>
				</section>

				<main className="mx-auto w-full max-w-5xl space-y-14 pt-14 pb-14">
					<section className="space-y-6">
						<LeaderboardPreview
							metric={metric}
							period={period}
							rows={rows}
							visibleRows={visibleRows}
							isLoading={isLeaderboardLoading}
							onLoadMore={() =>
								setVisibleRows((current) => Math.min(current + 25, rows.length))
							}
							onMetricChange={handleMetricChange}
							onPeriodChange={handlePeriodChange}
						/>
					</section>

					{!isLeaderboardLoading && rows.length > 0 && (
						<section className="space-y-6">
							<RisingSection rows={rows} />
						</section>
					)}

					<section className="space-y-6">
						<RecentlyVerifiedSection
							agents={allAgents}
							isLoading={isLeaderboardLoading}
						/>
					</section>
				</main>
				<PublicFooter />
			</div>
		</PublicPageLayout>
	);
}

function HeroTerminal({
	onAddAgent,
	onSellStartup,
}: {
	onAddAgent: () => void;
	onSellStartup: () => void;
}) {
	const sellStartupEnabled = useFeatureFlag(SELL_STARTUP_FLAG);

	return (
		<div className="flex flex-col gap-7">
			<div className="flex flex-col items-center gap-3 text-center sm:items-start sm:text-left">
				<span className="inline-flex items-center gap-1.5 self-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground sm:self-start">
					<LivePulse />
					<span className="text-foreground">Live</span>
				</span>
				<h1
					className="text-balance font-mono text-[clamp(1.75rem,4vw,2.75rem)] leading-[1.05] tracking-[-0.04em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					The database of AI native agents
				</h1>
			</div>

			<div className="flex flex-col gap-2">
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
					<div className="flex-1">
						<HomepageSearch />
					</div>
					<button
						type="button"
						onClick={onAddAgent}
						className="inline-flex min-h-11 min-w-[210px] shrink-0 self-stretch items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
					>
						<Plus className="size-3.5" aria-hidden="true" />
						Add your agent
					</button>
				</div>
				{sellStartupEnabled && (
					<button
						type="button"
						onClick={onSellStartup}
						className="inline-flex min-h-10 items-center justify-center gap-2 self-start border border-border bg-card px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
					>
						<Tag className="size-3.5" aria-hidden="true" />
						Sell Startup
					</button>
				)}
			</div>
		</div>
	);
}

function LivePulse({ className = "size-1.5" }: { className?: string }) {
	return (
		<span className={`relative inline-flex ${className}`} aria-hidden="true">
			<span className="absolute inline-flex size-full animate-ping rounded-full bg-[color:var(--ticker-up)] opacity-70" />
			<span
				className={`relative inline-flex ${className} rounded-full bg-[color:var(--ticker-up)]`}
			/>
		</span>
	);
}

function Sparkline({
	snapshots,
	firstChargeDate,
	width = 96,
	height = 24,
}: {
	snapshots: SnapshotRecord[];
	firstChargeDate?: Date | string | null;
	width?: number;
	height?: number;
}) {
	// Mirror the agent detail chart: when the first charge predates the earliest
	// snapshot, anchor the series at 0 so the climb from launch is visible
	// instead of a flat line that ignores the agent's history.
	const series = buildSparklineSeries(snapshots, firstChargeDate);
	if (series.length < 2) {
		return (
			<svg
				width={width}
				height={height}
				className="text-muted-foreground/25"
				aria-hidden="true"
			>
				<line
					x1={0}
					y1={height - 2}
					x2={width}
					y2={height - 2}
					stroke="currentColor"
					strokeWidth={1}
					strokeDasharray="2 3"
				/>
			</svg>
		);
	}
	const values = series.slice(-20);
	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min;
	const padY = 2;
	// A series with no movement (every value equal) sits on the bottom baseline,
	// where the minimum value would land for a non-flat series.
	const isFlat = range === 0;
	const points = values
		.map((v, i) => {
			const x = (i / (values.length - 1)) * width;
			const y = isFlat
				? height - padY
				: height - padY - ((v - min) / range) * (height - padY * 2);
			return `${x.toFixed(2)},${y.toFixed(2)}`;
		})
		.join(" ");
	const first = values[0];
	const last = values[values.length - 1];
	const trend = last > first ? "up" : last < first ? "down" : "flat";
	const stroke =
		trend === "up" ? "#10b981" : trend === "down" ? "#f43f5e" : "#94a3b8";
	const fill = trend === "up" ? "rgba(16,185,129,0.12)" : "rgba(244,63,94,0.1)";
	const areaPoints = `0,${height} ${points} ${width},${height}`;
	return (
		<svg
			width={width}
			height={height}
			className="overflow-visible"
			aria-hidden="true"
		>
			{trend !== "flat" && <polygon points={areaPoints} fill={fill} />}
			<polyline
				points={points}
				fill="none"
				stroke={stroke}
				strokeWidth={1.5}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

// Builds the cumulative-revenue series the sparkline plots. Mirrors the agent
// detail chart (normalizeRevenueTimeline), including its `firstChargeDate`
// anchor, so the trend climbs from launch instead of dipping on noisy MRR
// snapshots. Scoped to rendering only - growth/delta math keeps using raw
// snapshots.
function buildSparklineSeries(
	snapshots: SnapshotRecord[],
	firstChargeDate?: Date | string | null,
): number[] {
	if (snapshots.length === 0) {
		return [0, 0];
	}

	// Always plot cumulative gross revenue, mirroring the agent detail chart
	// (normalizeRevenueTimeline). Per-day grossRevenue is the only genuinely
	// historical signal we store; raw estimatedMrr snapshots carry the current
	// MRR and are noisy, so plotting them produced misleading dips on a trend
	// that is otherwise monotonically rising.
	const values = buildCumulativeRevenueSeries(snapshots);
	const drawableValues = values.length === 1 ? [values[0], values[0]] : values;
	if (!firstChargeDate || snapshots.length === 0) {
		return drawableValues;
	}
	// Compare on UTC-day granularity: snapshotDate is normalized to midnight
	// while firstChargeDate keeps its intra-day time, so a same-day launch (first
	// charge at 23:10, first snapshot stamped 00:00) would wrongly read as
	// "charge after snapshot" and skip the anchor, leaving the line pinned at the
	// backfilled lifetime lump. Anchor whenever the launch day is on/before the
	// first snapshot's day so the climb from 0 stays visible.
	const firstChargeDay = toUtcDayStart(firstChargeDate);
	const firstSnapshotDay = toUtcDayStart(snapshots[0]?.snapshotDate);
	if (
		firstChargeDay !== null &&
		firstSnapshotDay !== null &&
		firstChargeDay <= firstSnapshotDay
	) {
		return [0, ...drawableValues];
	}
	return drawableValues;
}

// Normalizes a date to the start of its UTC day (ms), or null if unparseable.
// Snapshot dates are stored at UTC midnight, so comparisons against the real
// firstChargeDate must drop the time-of-day to avoid same-day off-by-hours bugs.
function toUtcDayStart(value?: Date | string | null): number | null {
	if (!value) return null;
	const time = new Date(value).getTime();
	if (!Number.isFinite(time)) return null;
	return Math.floor(time / 86_400_000) * 86_400_000;
}

function buildCumulativeRevenueSeries(snapshots: SnapshotRecord[]) {
	let runningRevenue = 0;
	return snapshots.map((snapshot) => {
		runningRevenue += Math.max(0, snapshot.grossRevenue ?? 0);
		return runningRevenue;
	});
}

function LeaderboardPreview({
	metric,
	period,
	rows,
	visibleRows,
	isLoading,
	onLoadMore,
	onMetricChange,
	onPeriodChange,
}: {
	metric: MetricOption;
	period: PeriodOption;
	rows: LeaderboardRow[];
	visibleRows: number;
	isLoading: boolean;
	onLoadMore: () => void;
	onMetricChange: (metric: MetricOption) => void;
	onPeriodChange: (period: PeriodOption) => void;
}) {
	const currency = useAtomValue(currencyAtom);
	const sortedRows = [...rows]
		.filter((row) => metric !== "growth" || row.metric.mrr >= 100)
		.sort((left, right) => {
			const tierDelta = leaderboardTier(left) - leaderboardTier(right);
			if (tierDelta !== 0) return tierDelta;

			if (metric === "growth") {
				return right.growth[period] - left.growth[period];
			}

			return right.metric[metric] - left.metric[metric];
		});

	const displayedRows = sortedRows.slice(0, visibleRows);
	const remainingRows = sortedRows.length - displayedRows.length;

	return (
		<div className="text-foreground">
			<div className="flex flex-col gap-4 pb-5 md:flex-row md:items-end md:justify-between">
				<div className="flex items-baseline gap-3">
					<h2
						className="font-mono text-[clamp(1.125rem,2.2vw,1.5rem)] leading-none tracking-[-0.02em] text-foreground"
						style={{ fontWeight: 400 }}
					>
						<span className="text-brand">/</span>agents
					</h2>
				</div>

				<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
					<div className="grid grid-cols-2 gap-2 sm:flex">
						<LeaderboardSelect
							label="Metric"
							value={metric}
							options={METRIC_OPTIONS}
							onChange={(value) => onMetricChange(value as MetricOption)}
						/>
						<LeaderboardSelect
							label="Window"
							value={period}
							options={PERIOD_OPTIONS}
							onChange={(value) => onPeriodChange(value as PeriodOption)}
						/>
					</div>
				</div>
			</div>

			<div className="overflow-x-auto border-t border-border">
				<table className="w-full min-w-[640px] border-separate border-spacing-0">
					<thead>
						<tr className="text-left font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							<th className="w-[48px] p-2 font-normal sm:w-[56px]">#</th>
							<th className="p-2 font-normal">Agent</th>
							<th className="p-2 font-normal">Founder</th>
							<th className="p-2 font-normal">Trend</th>
							<th className="p-2 text-right font-normal">
								{metric === "growth" ? "Growth" : metric.toUpperCase()}
							</th>
							<th className="whitespace-nowrap p-2 text-right font-normal">
								{period === "all" ? "All-time" : `${period.slice(1)}d`}
							</th>
						</tr>
					</thead>
					<tbody>
						{isLoading && rows.length === 0 ? <LeaderboardLoadingRow /> : null}
						{!isLoading && rows.length === 0 ? (
							<tr>
								<td
									colSpan={6}
									className="border-t border-border px-6 py-10 text-center text-base text-muted-foreground"
								>
									No agents listed yet. Be the first.
								</td>
							</tr>
						) : null}
						{displayedRows.flatMap((row, index) => {
							const SPONSOR_ROW_INTERVAL = 12;
							const LEADERBOARD_COLUMNS = 6;
							const leaderboardRow = (
								<tr
									key={row.id}
									className="group transition-colors hover:bg-brand-soft"
								>
									<td className="border-t border-border/60 px-2 py-1.5 align-middle font-mono text-sm tabular-nums text-muted-foreground">
										{String(index + 1)}
									</td>
									<td className="border-t border-border/60 px-2 py-1.5 align-middle">
										<div className="flex items-center gap-3">
											<InitialAvatar
												name={row.displayName}
												category={
													coerceAgentCategories(row.categories)[0] ?? ""
												}
												logoUrl={row.logoUrl}
												mode={row.displayAvatarMode}
												size="agent"
											/>
											<Link
												to="/agents/$agentSlug"
												params={{ agentSlug: row.slug }}
												className="min-w-0 flex-1"
											>
												<div className="flex min-w-0 items-center gap-1.5">
													<p
														className="truncate text-base text-foreground transition-colors group-hover:text-brand"
														style={{ fontWeight: 400 }}
													>
														{row.displayName}
													</p>
													{row.isVerified && (
														<VerifiedBadge
															provider={row.verificationProvider}
														/>
													)}
												</div>
												<p className="line-clamp-1 text-sm text-muted-foreground">
													{row.displayDescription}
												</p>
											</Link>
										</div>
									</td>
									<td className="border-t border-border/60 px-2 py-1.5 align-middle">
										{row.isUnclaimed ? null : (
											<div className="flex items-center gap-2">
												<InitialAvatar
													name={row.displayFounderName}
													category={
														coerceAgentCategories(row.categories)[0] ?? ""
													}
													avatarUrl={row.displayFounderAvatarUrl}
													mode={
														row.displayAvatarMode === "stealth"
															? "stealth"
															: "brand"
													}
													size="founder"
												/>
												{row.displayFounderSlug ? (
													<Link
														to="/founders/$founderSlug"
														params={{ founderSlug: row.displayFounderSlug }}
														className="whitespace-nowrap text-sm text-foreground transition-colors hover:text-brand"
													>
														{row.displayFounderName}
													</Link>
												) : (
													<p className="whitespace-nowrap text-sm text-foreground">
														{row.displayFounderName}
													</p>
												)}
											</div>
										)}
									</td>
									<td className="border-t border-border/60 px-2 py-1.5 align-middle">
										<Sparkline
											snapshots={row.snapshots}
											firstChargeDate={row.firstChargeDate}
										/>
									</td>
									<td
										className="whitespace-nowrap border-t border-border/60 px-2 py-1.5 text-right align-middle font-mono text-base tabular-nums text-foreground"
										style={{ fontWeight: 400 }}
									>
										{metric === "growth" ? (
											<div className="flex flex-col items-end">
												<span>{formatGrowth(row.growth[period])}</span>
												{row.mrrDelta[period] !== 0 && (
													<span className="text-sm text-muted-foreground">
														{formatMrrDelta(row.mrrDelta[period], currency)}
													</span>
												)}
											</div>
										) : (
											formatCurrencyUtil(row.metric[metric], currency)
										)}
									</td>
									<td className="whitespace-nowrap border-t border-border/60 px-2 py-1.5 text-right align-middle">
										{period === "all" ? (
											<span className="font-mono text-base tabular-nums text-foreground">
												{formatCurrencyUtil(
													row.metric.allTimeRevenue,
													currency,
												)}
											</span>
										) : row.growth[period] !== 0 ? (
											<GrowthPill value={row.growth[period]} />
										) : (
											<span className="font-mono text-sm tabular-nums text-muted-foreground">
												0%
											</span>
										)}
									</td>
								</tr>
							);
							const atInterval = (index + 1) % SPONSOR_ROW_INTERVAL === 0;
							if (!atInterval) return [leaderboardRow];
							return [
								leaderboardRow,
								<PromoLeaderboardRow
									key={`promo-after-${row.id}`}
									colSpan={LEADERBOARD_COLUMNS}
									ordinal={Math.floor(index / SPONSOR_ROW_INTERVAL)}
								/>,
							];
						})}
					</tbody>
				</table>
			</div>
			{remainingRows > 0 && (
				<div className="border-t border-border pt-4">
					<button
						type="button"
						onClick={onLoadMore}
						className="inline-flex min-h-10 items-center font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96]"
					>
						&gt; Load more ({remainingRows} remaining)
					</button>
				</div>
			)}
		</div>
	);
}

function LeaderboardLoadingRow() {
	return (
		<tr>
			<td
				colSpan={6}
				className="px-6 py-10 text-center text-base text-muted-foreground"
			>
				Loading leaderboard...
			</td>
		</tr>
	);
}

function LeaderboardSelect({
	label,
	value,
	options,
	onChange,
}: {
	label: string;
	value: string;
	options: ReadonlyArray<{ id: string; label: string }>;
	onChange: (value: string) => void;
}) {
	return (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger
				aria-label={label}
				className="w-full min-w-0 rounded-none border-0 border-b border-border bg-transparent px-2 py-1.5 font-mono text-sm uppercase tracking-[0.12em] text-foreground shadow-none hover:text-brand focus-visible:ring-0 sm:min-w-[8rem] [&_svg]:text-muted-foreground"
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent
				position="popper"
				side="bottom"
				className="rounded-none border-border bg-popover font-mono text-sm uppercase tracking-[0.12em]"
			>
				{options.map((option) => (
					<SelectItem
						key={option.id}
						value={option.id}
						className="font-mono text-sm uppercase tracking-[0.12em] text-popover-foreground focus:bg-brand-soft focus:text-brand"
					>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

function GrowthPill({ value }: { value: number }) {
	if (value > 0) {
		return (
			<span className="font-mono text-sm tabular-nums text-[color:var(--ticker-up)]">
				▲ {formatGrowth(value)}
			</span>
		);
	}

	if (value < 0) {
		return (
			<span className="font-mono text-sm tabular-nums text-[color:var(--ticker-down)]">
				▼ {formatGrowth(value)}
			</span>
		);
	}

	return null;
}

function formatGrowth(value: number) {
	return `${value > 0 ? "+" : ""}${new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 0,
	}).format(value)}%`;
}

function RisingSection({ rows }: { rows: LeaderboardRow[] }) {
	const currency = useAtomValue(currencyAtom);

	// "Rising" requires an actual MRR increase measured against an earlier data
	// point. mrrDelta.d30 is only non-zero when there are >= 2 snapshots, so this
	// excludes brand-new agents that look like 100% growth merely because there
	// was no prior baseline to compare against.
	const risingRows = [...rows]
		.filter((row) => row.metric.mrr > 0 && row.mrrDelta.d30 > 0)
		.sort((a, b) => b.growth.d30 - a.growth.d30 || b.metric.mrr - a.metric.mrr)
		.slice(0, 10);

	if (risingRows.length === 0) return null;

	return (
		<div className="text-foreground">
			<div className="flex items-baseline gap-3 pb-5">
				<h2
					className="font-mono text-[clamp(1.125rem,2.2vw,1.5rem)] leading-none tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					<span className="text-brand">/</span>rising
				</h2>
			</div>
			<div className="border-t border-border">
				<table className="w-full border-separate border-spacing-0">
					<thead>
						<tr className="text-left font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							<th className="w-[48px] p-2 font-normal sm:w-[56px]">#</th>
							<th className="p-2 font-normal">Agent</th>
							<th className="hidden p-2 font-normal @xl/page:table-cell">
								Founder
							</th>
							<th className="p-2 text-right font-normal">30D</th>
							<th className="hidden py-2 pr-2 pl-8 text-right font-normal @2xl/page:table-cell">
								MRR
							</th>
						</tr>
					</thead>
					<tbody>
						{risingRows.map((row, index) => (
							<tr
								key={row.id}
								className="group transition-colors hover:bg-brand-soft"
							>
								<td className="border-t border-border/60 px-2 py-1.5 align-middle font-mono text-sm tabular-nums text-muted-foreground">
									{String(index + 1)}
								</td>
								<td className="border-t border-border/60 px-2 py-1.5 align-middle">
									<div className="flex items-center gap-3">
										<InitialAvatar
											name={row.displayName}
											category={coerceAgentCategories(row.categories)[0] ?? ""}
											logoUrl={row.logoUrl}
											mode={row.displayAvatarMode}
											size="agent"
										/>
										<Link
											to="/agents/$agentSlug"
											params={{ agentSlug: row.slug }}
											className="min-w-0 flex-1"
										>
											<p
												className="truncate text-base text-foreground transition-colors group-hover:text-brand"
												style={{ fontWeight: 400 }}
											>
												{row.displayName}
											</p>
											<p className="line-clamp-1 text-sm text-muted-foreground">
												{row.displayDescription}
											</p>
										</Link>
									</div>
								</td>
								<td className="hidden border-t border-border/60 px-2 py-1.5 align-middle @xl/page:table-cell">
									<div className="flex items-center gap-2">
										<InitialAvatar
											name={row.displayFounderName}
											category={coerceAgentCategories(row.categories)[0] ?? ""}
											avatarUrl={row.displayFounderAvatarUrl}
											mode={
												row.displayAvatarMode === "stealth"
													? "stealth"
													: "brand"
											}
											size="founder"
										/>
										{row.displayFounderSlug ? (
											<Link
												to="/founders/$founderSlug"
												params={{ founderSlug: row.displayFounderSlug }}
												className="whitespace-nowrap text-sm text-foreground transition-colors hover:text-brand"
											>
												{row.displayFounderName}
											</Link>
										) : (
											<p className="whitespace-nowrap text-sm text-foreground">
												{row.displayFounderName}
											</p>
										)}
									</div>
								</td>
								<td
									className="whitespace-nowrap border-t border-border/60 px-2 py-1.5 text-right align-middle"
									style={{ fontWeight: 400 }}
								>
									{row.growth.d30 !== 0 ? (
										<GrowthPill value={row.growth.d30} />
									) : (
										<span className="font-mono text-sm text-muted-foreground">
											-
										</span>
									)}
								</td>
								<td className="hidden border-t border-border/60 py-1.5 pr-2 pl-8 text-right align-middle font-mono text-sm tabular-nums text-muted-foreground @2xl/page:table-cell">
									{formatCurrencyUtil(row.metric.mrr, currency)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function leaderboardTier(row: {
	isUnclaimed: boolean;
	metric: { mrr: number };
}) {
	if (row.isUnclaimed) return 2;
	if (row.metric.mrr > 0) return 0;
	return 1;
}

function buildLeaderboardRows(agents: AgentRecord[]): LeaderboardRow[] {
	return agents
		.map((agent) => {
			const publicAgent = agent;
			const snapshots = normalizeSnapshots(agent.revenueSnapshots ?? []);

			return {
				categories: coerceAgentCategories(publicAgent.categories),
				displayName: publicAgent.displayName,
				displayDescription: publicAgent.displayDescription,
				displayFounderName: publicAgent.displayFounderName,
				displayFounderSlug: publicAgent.displayFounderSlug,
				displayFounderAvatarUrl: publicAgent.displayFounderAvatarUrl,
				displayAvatarMode: publicAgent.displayAvatarMode,
				id: publicAgent.id,
				isUnclaimed: publicAgent.isUnclaimed,
				isVerified: publicAgent.verificationStatus === "verified",
				verificationProvider: publicAgent.verificationProvider ?? null,
				logoUrl: publicAgent.logoUrl ?? null,
				metric: {
					arr: Math.max(publicAgent.estimatedMrr, 0) * 12,
					allTimeRevenue: Math.max(publicAgent.allTimeRevenue, 0),
					mrr: Math.max(publicAgent.estimatedMrr, 0),
				},
				growth: {
					all: getGrowthValue(publicAgent, snapshots, "all"),
					d30: getRevenueMomentum(snapshots, "d30"),
				},
				mrrDelta: {
					all: getMrrDeltaValue(snapshots, "all"),
					d30: getMrrDeltaValue(snapshots, "d30"),
				},
				slug: publicAgent.displayPathSegment,
				snapshots,
				firstChargeDate: publicAgent.firstChargeDate ?? null,
				updatedAtLabel: formatTimestamp(publicAgent.lastSyncedAt),
				lastSyncedAt: publicAgent.lastSyncedAt,
			};
		})
		.sort((left, right) => {
			const tierDelta = leaderboardTier(left) - leaderboardTier(right);
			if (tierDelta !== 0) return tierDelta;

			return right.metric.mrr - left.metric.mrr;
		});
}

function normalizeSnapshots(snapshots: SnapshotRecord[]) {
	return snapshots.toSorted(
		(left, right) =>
			new Date(left.snapshotDate).getTime() -
			new Date(right.snapshotDate).getTime(),
	);
}

function getGrowthValue(
	agent: PublicAgentView,
	snapshots: SnapshotRecord[],
	period: PeriodOption,
) {
	if (period === "d30" && typeof agent.momMrrChangePct === "number") {
		return Math.round(agent.momMrrChangePct);
	}

	const metricKey = "estimatedMrr";
	if (snapshots.length < 2) {
		return 0;
	}

	const latest = snapshots[snapshots.length - 1];
	const baseline = findBaselineSnapshot(snapshots, period);

	if (!baseline || !latest) {
		return 0;
	}

	return Math.round(
		calculatePercentChange(baseline[metricKey] ?? 0, latest[metricKey] ?? 0),
	);
}

// Snapshots arrive at irregular intervals, so the window baseline is picked by
// actual date (the most recent snapshot at or before the cutoff) rather than by
// array index. Falls back to the earliest snapshot when history is shorter than
// the window.
const PERIOD_WINDOW_DAYS: Record<Exclude<PeriodOption, "all">, number> = {
	d30: 30,
};

// Revenue momentum: percent change between revenue earned in the trailing
// window and the window immediately before it. Per-day grossRevenue is the only
// genuinely historical revenue signal we store (snapshots all carry the current
// estimatedMrr, so MRR-based growth would always be 0), which makes this the
// reliable basis for window-over-window growth.
function getRevenueMomentum(
	snapshots: SnapshotRecord[],
	period: Exclude<PeriodOption, "all">,
) {
	const windowMs = PERIOD_WINDOW_DAYS[period] * 24 * 60 * 60 * 1000;
	const now = Date.now();
	const recentCutoff = now - windowMs;
	const priorCutoff = now - windowMs * 2;

	let recent = 0;
	let prior = 0;
	for (const snapshot of snapshots) {
		const time = new Date(snapshot.snapshotDate).getTime();
		const revenue = Math.max(0, snapshot.grossRevenue ?? 0);
		if (time >= recentCutoff) {
			recent += revenue;
		} else if (time >= priorCutoff) {
			prior += revenue;
		}
	}

	return Math.round(calculatePercentChange(prior, recent));
}

function findBaselineSnapshot(
	snapshots: SnapshotRecord[],
	period: PeriodOption,
) {
	if (period === "all") {
		return snapshots[0];
	}

	const latest = snapshots[snapshots.length - 1];
	const cutoff =
		new Date(latest.snapshotDate).getTime() -
		PERIOD_WINDOW_DAYS[period] * 24 * 60 * 60 * 1000;

	let baseline = snapshots[0];
	for (const snapshot of snapshots) {
		if (new Date(snapshot.snapshotDate).getTime() <= cutoff) {
			baseline = snapshot;
		} else {
			break;
		}
	}

	return baseline;
}

function calculatePercentChange(previous: number, current: number) {
	if (previous === 0) {
		return current === 0 ? 0 : 100;
	}

	return ((current - previous) / Math.abs(previous)) * 100;
}

function getMrrDeltaValue(
	snapshots: SnapshotRecord[],
	period: PeriodOption,
): number {
	if (snapshots.length < 2) {
		return 0;
	}

	const metricKey = "estimatedMrr";
	const latest = snapshots[snapshots.length - 1];
	const baseline = findBaselineSnapshot(snapshots, period);

	if (!baseline || !latest) {
		return 0;
	}

	return Math.round((latest[metricKey] ?? 0) - (baseline[metricKey] ?? 0));
}

function formatMrrDelta(value: number, currency: Currency) {
	const sign = value > 0 ? "+" : "";
	return `${sign}${formatCurrencyUtil(value, currency)}/mo`;
}

function formatTimestamp(value?: Date | string) {
	if (!value) {
		return null;
	}

	return new Intl.DateTimeFormat("en-US", {
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		month: "short",
		year: "numeric",
	}).format(new Date(value));
}

function formatVerifiedAgo(lastVerifiedAt: Date | string): string {
	const ms = Date.now() - new Date(lastVerifiedAt).getTime();
	const minutes = Math.max(0, Math.floor(ms / (1000 * 60)));
	if (minutes < 60) {
		const value = Math.max(1, minutes);
		return `${value} ${value === 1 ? "minute" : "minutes"} ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
	const days = Math.floor(hours / 24);
	return `${days} ${days === 1 ? "day" : "days"} ago`;
}

type RecentlyVerifiedRow = {
	categories: string[];
	displayAvatarMode: PublicAgentView["displayAvatarMode"];
	displayDescription: string;
	displayInitials: string;
	displayName: string;
	id: string;
	isUnclaimed: boolean;
	recentAt: Date | string;
	logoUrl?: string | null;
	mrr: number;
	slug: string;
};

function buildRecentlyVerifiedRows(
	agents: AgentRecord[],
): RecentlyVerifiedRow[] {
	return agents
		.filter((agent) => agent.isStealth !== true)
		.map((agent) => {
			const publicAgent = agent;
			return {
				categories: coerceAgentCategories(publicAgent.categories),
				displayAvatarMode: publicAgent.displayAvatarMode,
				displayDescription: publicAgent.displayDescription,
				displayInitials: publicAgent.displayInitials,
				displayName: publicAgent.displayName,
				id: publicAgent.id,
				isUnclaimed: publicAgent.isUnclaimed,
				recentAt: publicAgent.createdAt,
				logoUrl: publicAgent.logoUrl ?? null,
				mrr: Math.max(publicAgent.estimatedMrr, 0),
				slug: publicAgent.displayPathSegment,
			};
		})
		.filter((row) => row.recentAt != null)
		.sort((a, b) => {
			const unclaimedDelta = Number(a.isUnclaimed) - Number(b.isUnclaimed);
			if (unclaimedDelta !== 0) return unclaimedDelta;

			return new Date(b.recentAt).getTime() - new Date(a.recentAt).getTime();
		})
		.slice(0, 8);
}

function RecentlyVerifiedCard({ row }: { row: RecentlyVerifiedRow }) {
	const currency = useAtomValue(currencyAtom);
	const recentLabel = formatVerifiedAgo(row.recentAt);
	const recentMsAgo = Date.now() - new Date(row.recentAt).getTime();
	const isLive = recentMsAgo >= 0 && recentMsAgo < 1000 * 60;

	return (
		<Link
			to="/agents/$agentSlug"
			params={{ agentSlug: row.slug }}
			className="group flex w-[260px] shrink-0 snap-start flex-col gap-3 border border-border p-4 transition-colors hover:border-brand/40 hover:bg-brand-soft"
		>
			<div className="flex items-start gap-3">
				<InitialAvatar
					name={row.displayName}
					category={coerceAgentCategories(row.categories)[0] ?? ""}
					logoUrl={row.logoUrl}
					mode={row.displayAvatarMode}
					size="agent"
				/>
				<div className="min-w-0 flex-1">
					<p
						className="truncate text-base text-foreground transition-colors group-hover:text-brand"
						style={{ fontWeight: 400 }}
					>
						{row.displayName}
					</p>
					<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
						{coerceAgentCategories(row.categories)
							.map(getAgentCategoryLabel)
							.join(" · ")}
					</span>
				</div>
			</div>
			<p className="line-clamp-2 min-h-[2.2em] text-sm leading-[1.4] text-muted-foreground">
				{row.displayDescription}
			</p>
			<div className="mt-auto flex items-baseline justify-between border-t border-border/60 pt-3">
				<span className="font-mono text-base leading-none tabular-nums text-foreground">
					{formatCompactCurrencyUtil(row.mrr, currency)}
				</span>
				<span className="flex items-center gap-1.5 font-mono text-sm tracking-[0.12em] text-muted-foreground">
					{isLive ? (
						<span className="relative flex size-1.5" aria-hidden>
							<span className="absolute inline-flex size-full animate-ping rounded-full bg-brand opacity-60" />
							<span className="relative inline-flex size-1.5 rounded-full bg-brand" />
						</span>
					) : null}
					{recentLabel}
				</span>
			</div>
		</Link>
	);
}

function RecentlyVerifiedSection({
	agents,
	isLoading,
}: {
	agents: AgentRecord[];
	isLoading: boolean;
}) {
	const rows = buildRecentlyVerifiedRows(agents);

	if (!isLoading && rows.length === 0) return null;

	return (
		<div className="text-foreground">
			<div className="flex items-baseline gap-3 pb-5">
				<h2
					className="font-mono text-[clamp(1.125rem,2.2vw,1.5rem)] leading-none tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					<span className="text-brand">/</span>recent
				</h2>
			</div>
			<div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 scrollbar-none">
				{isLoading
					? [
							"recent-skeleton-1",
							"recent-skeleton-2",
							"recent-skeleton-3",
							"recent-skeleton-4",
						].map((key) => (
							<div
								key={key}
								className="flex w-[260px] shrink-0 snap-start flex-col gap-3 border border-border p-4"
							>
								<div className="flex items-start gap-3">
									<div className="size-9 shrink-0 animate-pulse rounded-full bg-muted" />
									<div className="flex-1 space-y-1.5">
										<div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
										<div className="h-2 w-1/3 animate-pulse rounded bg-muted" />
									</div>
								</div>
								<div className="h-3 w-full animate-pulse rounded bg-muted" />
								<div className="size-3/4 animate-pulse rounded bg-muted" />
								<div className="mt-auto flex items-baseline justify-between border-t border-border/60 pt-3">
									<div className="h-3 w-12 animate-pulse rounded bg-muted" />
									<div className="h-2 w-10 animate-pulse rounded bg-muted" />
								</div>
							</div>
						))
					: rows.map((row) => <RecentlyVerifiedCard key={row.id} row={row} />)}
			</div>
		</div>
	);
}
